import axios from "axios"
import { z } from "zod"
import _ from "lodash"
import { I18n } from "@/lib/i18n_instance"

const t = I18n.global.t

export enum ConventionUserStatus {
  pending = "In attesa",
  active = "Attivo",
  deactivated = "Inattivo"
}
export const ConventionUserStatusKeys = Object.keys(ConventionUserStatus) as [keyof typeof ConventionUserStatus]

export const conventionUserSchema = z.object({
  id: z.number(),
  convention_id: z.string().optional(),
  username: z.string().email(),
  description: z.string().optional(),
  created_at: z.string().datetime({ offset: true }).optional(),
  updated_at: z.string().datetime({ offset: true }).optional(),
  confirmation_sent_at: z.string().datetime({ offset: true }).nullable().optional(),
  confirmed_at: z.string().datetime({ offset: true }).nullable().optional(),
  deactivated_at: z.string().datetime({ offset: true }).nullable().optional(),
  status: z.enum(ConventionUserStatusKeys).default("pending"),
  resource: z
    .object({
      id: z.number(),
      name: z.string(),
      surname: z.string(),
      telephone: z.string().nullable().optional(),
      fax: z.string().nullable().optional(),
      email: z.string().email()
    })
    .optional()
})

export type ConventionUser = z.infer<typeof conventionUserSchema>

export const extendedConventionUserSchema = conventionUserSchema.extend({
  roles: z.lazy(() => z.string().array().default([])).optional()
})

export type ExtendedConventionUser = z.infer<typeof extendedConventionUserSchema>

export const editableConventionUserSchema = z.object({
  name: z.string({ required_error: t("conventions.form.errors.required_field") }).trim().min(1, { message: t("conventions.form.errors.required_field") }),
  surname: z.string({ required_error: t("conventions.form.errors.required_field") }).trim().min(1, { message: t("conventions.form.errors.required_field") }),
  telephone: z.string().nullable().optional(),
  role: z.enum(["admin_convention_users", "super_reader", "reader"], { required_error: t("conventions.form.errors.required_field") })
})

export type EditableConventionUser = z.infer<typeof editableConventionUserSchema>

export const createConventionUserSchema = editableConventionUserSchema.extend({
  username: z
    .string({ required_error: t("conventions.form.errors.required_field") })
    .email({ message: t("conventions.form.errors.invalid_email") })
    .refine(
      async (username) => {
        if (z.string().email().safeParse(username).success) {
          const isTaken = await debouncedCheckUsernames(username)
          return !isTaken
        }
      },
      {
        message: t("conventions.form.errors.taken_email")
      }
    )
})

export type CreateConventionUser = z.infer<typeof createConventionUserSchema>

export const updateConventionUserSchema = editableConventionUserSchema.extend({
  id: z.number(),
  username: z.string().email()
})

export type UpdateConventionUser = z.infer<typeof updateConventionUserSchema>

export const createConventionUser = async (
  convention_user: z.infer<typeof createConventionUserSchema>
): Promise<ConventionUser> => {
  return axios
    .post("/api/convention_users", convention_user)
    .then((response) => {
      return conventionUserSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const updateConventionUser = async (
  convention_user: z.infer<typeof updateConventionUserSchema>
): Promise<ConventionUser> => {
  return axios
    .patch(`/api/convention_users/${convention_user.id}`, convention_user)
    .then((response) => {
      return conventionUserSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const destroyConventionUser = async (id: number): Promise<ConventionUser> => {
  return axios.delete(`/api/convention_users/${id}`)
}

export const checkUsernames = async (username: string): Promise<boolean> => {
  return axios
    .get("/api/conventions/check_usernames", { params: { username: username } })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const confirmConventionUser = async (username: string): Promise<ConventionUser> => {
  return axios
    .patch(`/api/convention_users/${username}/confirm`)
    .then((response) => {
      return conventionUserSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const activateConventionUser = async (username: string): Promise<ConventionUser> => {
  return axios
    .patch(`/api/convention_users/${username}/activate`)
    .then((response) => {
      return conventionUserSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

export const deactivateConventionUser = async (username: string): Promise<ConventionUser> => {
  return axios
    .patch(`/api/convention_users/${username}/deactivate`)
    .then((response) => {
      return conventionUserSchema.parse(response.data)
    })
    .catch((error) => {
      if (!axios.isCancel(error)) console.error(error)
      throw error
    })
}

const debouncedCheckUsernames = _.debounce(checkUsernames, 300)
